import React, { useState, useEffect } from "react";
import "./AdminDashboard.css";
import { FaUserFriends } from "react-icons/fa";
import {
  FiBarChart2,
  FiDownload,
  FiMenu,
  FiSearch,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";
import { BASE_URL } from "./Config";
import { useNavigate } from "react-router-dom";
import { isSuperAdmin, isSubAdmin } from "../utils/auth";
import logoImage from "../resources/1000028180.jpeg";

export default function AdminDashboard() {
  const [searchOrg, setSearchOrg] = useState("");
  const [searchSport, setSearchSport] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [activeTestId, setActiveTestId] = useState(null);
  const [analysisTestId, setAnalysisTestId] = useState(null);
  const [openAnalysisDropdown, setOpenAnalysisDropdown] = useState(null);
  const [openActionsDropdown, setOpenActionsDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".actions-dropdown-menu") &&
        !event.target.closest(".analysis-dropdown-menu") &&
        !event.target.closest("button")
      ) {
        setOpenActionsDropdown(null);
        setOpenAnalysisDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const downloadFile = async (testId, format) => {
    const token = localStorage.getItem("admin_token");
    const endpoint =
      format === "pdf"
        ? `${BASE_URL}/api/admin/download_pdf/${testId}`
        : `${BASE_URL}/api/admin/download_word/${testId}`;

    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();

      // Extract filename from Content-Disposition header
      let filename = `Test_${testId}.${format === "pdf" ? "pdf" : "docx"}`;
      const disposition = response.headers.get("Content-Disposition");

      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Download failed: " + error.message);
    }
  };

  // Delete user and all their test data
  const deleteTest = async (testId) => {
    const token = localStorage.getItem("admin_token");

    try {
      const response = await fetch(
        `${BASE_URL}/api/test/delete_test/${testId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete test.");
      }

      // Remove the deleted test from the local state
      setData((prevData) =>
        prevData.map((admin) => ({
          ...admin,
          tests: admin.tests.filter((test) => test.test_id !== testId),
        }))
      );
    } catch (err) {
      console.error("Delete failed:", err.message);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");

    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${BASE_URL}/api/admin/show_test_result`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        setData(json); // Expecting array of users with 'tests'
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const logout = () => {
    navigate("/logout");
  };

  const filteredData = data.filter(
    (entry) =>
      entry.organization_name
        ?.toLowerCase()
        .includes(searchOrg.toLowerCase()) ||
      entry.sport?.toLowerCase().includes(searchOrg.toLowerCase())
  );

  const startAnalysis = (testId) => {
    setOpenAnalysisDropdown((prev) => (prev === testId ? null : testId));
  };

  const handleDropdownClick = (testId, event, type) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    if (type === "actions") {
      setOpenActionsDropdown(openActionsDropdown === testId ? null : testId);
      if (openActionsDropdown !== testId) {
        setDropdownPosition({
          top: rect.bottom + 5,
          left: rect.left,
        });
      }
    } else if (type === "analysis") {
      setOpenAnalysisDropdown(openAnalysisDropdown === testId ? null : testId);
      if (openAnalysisDropdown !== testId) {
        setDropdownPosition({
          top: rect.bottom + 5,
          left: rect.left,
        });
      }
    }
  };

  const generateAndDownloadAnalysis = async (testId, format) => {
    const token = localStorage.getItem("admin_token");
    setAnalysisLoading(true);
    setActiveTestId(testId);

    const endpoint =
      format === "pdf"
        ? `${BASE_URL}/api/admin/gen_analysis/pdf/${testId}`
        : `${BASE_URL}/api/admin/gen_analysis/word/${testId}`;

    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to generate analysis");
      }

      const blob = await response.blob();

      let filename = `Analysis_${testId}.${format === "pdf" ? "pdf" : "docx"}`;
      const disposition = response.headers.get("Content-Disposition");
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // close dropdown
      setOpenAnalysisDropdown(null);
    } catch (error) {
      alert("Analysis Error: " + error.message);
    } finally {
      setAnalysisLoading(false);
      setActiveTestId(null);
    }
  };

  // // Handler for actions dropdown
  const handleDownloadAction = (testId, format) => {
    downloadFile(testId, format);
    setOpenActionsDropdown(null);
  };

  const handleDeleteClick = (testId) => {
    setTestToDelete(testId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!testToDelete) return;
    await deleteTest(testToDelete);
    setShowDeleteModal(false);
    setTestToDelete(null);
  };

  // Download Users CSV function
  const downloadUsersCSV = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      if (!token) {
        alert("No authentication token found");
        return;
      }

      console.log("Starting download users CSV...");
      console.log("Token:", token ? "Present" : "Missing");
      console.log("API URL:", `${BASE_URL}/api/admin/download_users_csv`);

      const response = await fetch(`${BASE_URL}/api/admin/download_users_csv`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Response error text:", errorText);
        throw new Error(
          `Failed to download CSV file: ${response.status} ${response.statusText}`
        );
      }

      console.log("Response OK, getting blob...");
      const blob = await response.blob();
      console.log("Blob size:", blob.size);
      console.log("Blob type:", blob.type);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "users_data.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log("Download completed successfully");
    } catch (error) {
      console.error("Download users CSV error:", error);
      alert("Error downloading CSV: " + error.message);
    }
  };

  // Download Tests CSV function
  const downloadTestsCSV = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      if (!token) {
        alert("No authentication token found");
        return;
      }

      console.log("Starting download tests CSV...");
      console.log("Token:", token ? "Present" : "Missing");
      console.log("API URL:", `${BASE_URL}/api/admin/download_tests_csv`);

      const response = await fetch(`${BASE_URL}/api/admin/download_tests_csv`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Response error text:", errorText);
        throw new Error(
          `Failed to download tests CSV file: ${response.status} ${response.statusText}`
        );
      }

      console.log("Response OK, getting blob...");
      const blob = await response.blob();
      console.log("Blob size:", blob.size);
      console.log("Blob type:", blob.type);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "tests_results.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log("Download completed successfully");
    } catch (error) {
      console.error("Download tests CSV error:", error);
      alert("Error downloading tests CSV: " + error.message);
    }
  };

  const menuBtnStyle = {
    background: "none",
    border: "solid 1px #f1f1f1",
    textAlign: "left",
    padding: "12px 20px",
    fontSize: "1rem",
    cursor: "pointer",
    width: "100%",
    transition: "background 0.18s",
    borderBottom: "1px solid #f1f1f1",
    color: "gray",
  };

  if (analysisLoading) {
    return (
      <div className="loader-overlay">
        <div className="loader-content">
          <center>
            <h2>Analyzing Test...</h2>
            <div className="spinner" />
          </center>
        </div>

        {/* Footer */}
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
            padding: "12px 0",
            textAlign: "center",
            fontFamily: "Poppins, sans-serif",
            zIndex: 100,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "rgba(75, 75, 75, 0.8)",
              fontWeight: 400,
            }}
          >
            © 2025 All rights reserved Power Mind Metrics
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-bg">
      <div
        className="org-header"
        style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}
      >
        <div
          className="header-content"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div className="header-logo-container">
            <img
              src={logoImage}
              alt="Power Mind Metrics Logo"
              style={{ height: "90px", width: "100px", marginRight: "10px" }}
            />
            <h1 className="header-logo">Power Mind Metrics</h1>
          </div>
          <nav className="header-nav" style={{ marginLeft: "auto" }}>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="menu-button"
              aria-label="Open menu"
            >
              <FiMenu size={24} />
            </button>
          </nav>
        </div>
        {menuOpen && (
          <div className="menu-dropdown">
            {isSuperAdmin() ? (
              <>
                <button
                  style={menuBtnStyle}
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/admin");
                  }}
                >
                  Admin Dashboard
                </button>
                <button
                  style={menuBtnStyle}
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/admin/sub-admins");
                  }}
                >
                  Manage Subadmin
                </button>
                <button
                  style={menuBtnStyle}
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/manage-users");
                  }}
                >
                  Manage Users
                </button>
                <button
                  style={menuBtnStyle}
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/organizations");
                  }}
                >
                  Manage Organization
                </button>
              </>
            ) : null}

            <button
              style={menuBtnStyle}
              onClick={() => {
                setMenuOpen(false);
                navigate("/graph-report");
              }}
            >
              Graph Report
            </button>

            <button
              style={menuBtnStyle}
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
            >
              Logout
            </button>
          </div>
        )}
      </div>

      <div style={{ marginTop: "84px" }}></div>
      {/* admin dashboard card */}
      <div className="admin-dashboard-card" style={{ marginBottom: "40px" }}>
        <div
          className="admin-header-row"
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <div
            className="admin-header-title"
            style={{
              fontSize: "1.5rem",
              fontWeight: 600,
              /* color: #222; */
              color: "rgba(75, 75, 75, 1)",
              letterSpacing: "0.01em",
            }}
          >
            Admin Dashboard
          </div>
          <div className="admin-dashboard-searchbar" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* <FiSearch
              style={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#bdbdbd",
              }}
            /> */}
            <input
              type="text"
              placeholder="Search organisation or sports"
              className="admin-dashboard-searchinput"
              value={searchOrg}
              onChange={(e) => setSearchOrg(e.target.value)}
              style={{
                padding: "10px 16px 10px 40px",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                fontSize: "1rem",
                width: 320,
                background: "#f8fafc",
                outline: "none",
              }}
            />
            <button
              onClick={downloadTestsCSV}
              style={{
                background: '#11998e',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s',
                height: '40px',
                minWidth: '160px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseOver={(e) => e.target.style.background = '#0e857b'}
              onMouseOut={(e) => e.target.style.background = '#11998e'}
            >
              <FiDownload />
              Download Result CSV
            </button>
          </div>
        </div>
        <div className="admin-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  Index <span style={{ color: "#bdbdbd" }}></span>
                </th>
                <th>
                  Full Name <span style={{ color: "#bdbdbd" }}></span>
                </th>
                <th>
                  Organization <span style={{ color: "#bdbdbd" }}></span>
                </th>
                <th>
                  Sport <span style={{ color: "#bdbdbd" }}></span>
                </th>
                <th>
                  Date of Birth <span style={{ color: "#bdbdbd" }}></span>
                </th>
                <th>
                  Test Type <span style={{ color: "#bdbdbd" }}></span>
                </th>
                <th>
                  Assessment Date <span style={{ color: "#bdbdbd" }}></span>
                </th>
                <th>Actions</th>
                <th>Analysis</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.flatMap((admin, idx) =>
                  admin.tests.map((test, tIdx) => {
                    const rowNumber = filteredData.slice(0, idx).reduce((acc, prevAdmin) => acc + prevAdmin.tests.length, 0) + tIdx + 1;
                    return (
                      <tr key={`${idx}-${tIdx}`}>
                        <td>{rowNumber}</td>
                        <td>{admin.full_name}</td>
                      <td>{admin.organization_name}</td>
                      <td>{admin.sport}</td>
                      <td>{admin.date_of_birth}</td>
                      <td>{test.test_type}</td>
                      <td>{test.taken_at}</td>
                      <td
                        className="actions-cell"
                        style={{ textAlign: "center" }}
                      >
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <button
                            className="org-icon-btn"
                            title="Download"
                            onClick={(e) =>
                              handleDropdownClick(test.test_id, e, "actions")
                            }
                          >
                            <FiDownload size={18} />
                          </button>
                          {openActionsDropdown === test.test_id && (
                            <>
                              {/* Overlay */}
                              <div
                                style={{
                                  position: "fixed",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: "rgba(0, 0, 0, 0.5)",
                                  zIndex: 9998,
                                }}
                                onClick={() => setOpenActionsDropdown(null)}
                              />
                              {/* Centered Card */}
                              <div
                                style={{
                                  position: "fixed",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  background: "#fff",
                                  borderRadius: "12px",
                                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                                  padding: "32px",
                                  minWidth: "400px",
                                  zIndex: 9999,
                                  textAlign: "center",
                                }}
                              >
                                <h3
                                  style={{
                                    margin: "0 0 16px 0",
                                    fontSize: "20px",
                                    fontWeight: "600",
                                    color: "#333",
                                  }}
                                >
                                  Download Test Report
                                </h3>
                                <p
                                  style={{
                                    margin: "0 0 24px 0",
                                    fontSize: "14px",
                                    color: "#666",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  Choose your preferred format to download the test report.
                                </p>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "16px",
                                    justifyContent: "center",
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      handleDownloadAction(test.test_id, "pdf")
                                    }
                                    style={{
                                      background: "#17c3b2",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "8px",
                                      padding: "12px 24px",
                                      fontSize: "14px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      transition: "background 0.2s",
                                      minWidth: "120px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = "#0e857b";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = "#17c3b2";
                                    }}
                                  >
                                    Download PDF
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDownloadAction(test.test_id, "word")
                                    }
                                    style={{
                                      background: "#6c757d",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "8px",
                                      padding: "12px 24px",
                                      fontSize: "14px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      transition: "background 0.2s",
                                      minWidth: "120px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = "#545b62";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = "#6c757d";
                                    }}
                                  >
                                    Download Word
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        <button
                          className="org-icon-btn org-delete-icon-btn"
                          title="Delete"
                          onClick={() => handleDeleteClick(test.test_id)}
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <button
                            className="start-analysis-btn"
                            onClick={(e) =>
                              handleDropdownClick(test.test_id, e, "analysis")
                            }
                            disabled={
                              analysisLoading && activeTestId === test.test_id
                            }
                          >
                            {analysisLoading && activeTestId === test.test_id
                              ? "Analyzing..."
                              : "Start Analysis"}
                          </button>
                          {openAnalysisDropdown === test.test_id && (
                            <>
                              {/* Overlay */}
                              <div
                                style={{
                                  position: "fixed",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: "rgba(0, 0, 0, 0.5)",
                                  zIndex: 9998,
                                }}
                                onClick={() => setOpenAnalysisDropdown(null)}
                              />
                              {/* Centered Card */}
                              <div
                                style={{
                                  position: "fixed",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  background: "#fff",
                                  borderRadius: "12px",
                                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                                  padding: "32px",
                                  minWidth: "400px",
                                  zIndex: 9999,
                                  textAlign: "center",
                                }}
                              >
                                <h3
                                  style={{
                                    margin: "0 0 16px 0",
                                    fontSize: "20px",
                                    fontWeight: "600",
                                    color: "#333",
                                  }}
                                >
                                  Download Analysis Report
                                </h3>
                                <p
                                  style={{
                                    margin: "0 0 24px 0",
                                    fontSize: "14px",
                                    color: "#666",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  Choose your preferred format to download the analysis report for this test.
                                </p>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "16px",
                                    justifyContent: "center",
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      generateAndDownloadAnalysis(
                                        test.test_id,
                                        "pdf"
                                      )
                                    }
                                    style={{
                                      background: "#17c3b2",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "8px",
                                      padding: "12px 24px",
                                      fontSize: "14px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      transition: "background 0.2s",
                                      minWidth: "120px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = "#0e857b";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = "#17c3b2";
                                    }}
                                  >
                                    Download PDF
                                  </button>
                                  <button
                                    onClick={() =>
                                      generateAndDownloadAnalysis(
                                        test.test_id,
                                        "word"
                                      )
                                    }
                                    style={{
                                      background: "#6c757d",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "8px",
                                      padding: "12px 24px",
                                      fontSize: "14px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      transition: "background 0.2s",
                                      minWidth: "120px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = "#545b62";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = "#6c757d";
                                    }}
                                  >
                                    Download Word
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    style={{ textAlign: "center", color: "#888" }}
                  >
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showDeleteModal && (
        <div className="subadmin-modal-overlay">
          <div
            className="subadmin-modal"
            style={{ maxWidth: 400, height: "auto", gap: 24 }}
          >
            <h3 className="subadmin-modal-title" style={{ color: "#b91c1c" }}>
              Delete Test
            </h3>
            <div>
              Are you sure you want to <b>delete this test?</b>
            </div>
            <div
              className="subadmin-modal-actions"
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <button
                type="button"
                className="subadmin-modal-cancel"
                onClick={() => {
                  setShowDeleteModal(false);
                  setTestToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="subadmin-modal-submit"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Footer */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderTop: "1px solid rgba(0, 0, 0, 0.1)",
          padding: "12px 0",
          textAlign: "center",
          fontFamily: "Poppins, sans-serif",
          zIndex: 100,
          // marginTop: "50px",
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: "14px",
            color: "rgba(75, 75, 75, 0.8)",
            fontWeight: 400,
          }}
        >
          © 2025 All rights reserved Power Mind Metrics
        </p>
      </div>
    </div>
  );
}
